import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import databaseAPI from '../api/databaseAPI';
import {
  downloadTrackingExportFromServer,
} from '../utils/trackingExport';
import '../styles/OrderTracking.css';

const STEP_COLUMNS = [
  { code: 'unfulfilled', label: 'Order Placed' },
  { code: 'preorder_received', label: 'Preorder Placed' },
  { code: 'progress', label: 'Production Update' },
  { code: 'delay', label: 'Delay Update' },
  { code: 'stock_in', label: 'Stock Arrived' },
  { code: 'packing', label: 'Packing Started' },
  { code: 'shipping_domestic', label: 'Shipped Domestic' },
  { code: 'shipping_international', label: 'Shipped Intl' },
  { code: 'arrived_at_destination', label: 'Arrived Dest.' },
  { code: 'out_of_delivery', label: 'Out for Delivery' },
  { code: 'destoryed', label: 'Customs Cleared' },
  { code: 'delivered', label: 'Delivered' },
  { code: 'review', label: 'Review Request' },
  { code: 'dispute', label: 'Issue Reported' },
  { code: 'replacement_approved', label: 'Repl. Approved' },
  { code: 'replacement_packing', label: 'Repl. Packed' },
  { code: 'replacement_shipping_domestic', label: 'Repl. Shipped Dom.' },
  { code: 'replacement_shipping_international', label: 'Repl. Shipped Intl' },
  { code: 'replacement_delivered', label: 'Repl. Delivered' },
  { code: 'refunded', label: 'Refund Processed' },
];

function formatDateTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const HIDE_EMAIL_NOT_SENT_STEPS = new Set(['unfulfilled', 'preorder_received']);

function StepCell({ step, inPipeline }) {
  if (!inPipeline) {
    return <div className="ot-step-cell ot-step-invalid">Not valid</div>;
  }
  if (!step) {
    return <div className="ot-step-cell ot-step-pending">Not reached</div>;
  }
  if (!step.reached) {
    return <div className="ot-step-cell ot-step-pending">Not reached</div>;
  }

  const hideEmailNotSent = HIDE_EMAIL_NOT_SENT_STEPS.has(step.code);

  return (
    <div className="ot-step-cell ot-step-reached">
      <div className="ot-step-tick" title="Reached" aria-label="Reached">
        <svg viewBox="0 0 20 20" width="26" height="26" aria-hidden="true">
          <circle cx="10" cy="10" r="9" fill="#047857" />
          <path
            d="M6 10.2l2.4 2.4L14 7.2"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {step.changed_at ? (
        <div className="ot-step-date">{formatDateTime(step.changed_at)}</div>
      ) : null}
      {step.email_sent || !hideEmailNotSent ? (
        <div className={`ot-step-email ${step.email_sent ? 'sent' : 'skipped'}`}>
          {step.email_sent ? (
            <>
              <span>Email sent</span>
              {step.email_sent_at ? (
                <span className="ot-step-email-date">{formatDateTime(step.email_sent_at)}</span>
              ) : null}
            </>
          ) : (
            <span>Status skip</span>
          )}
        </div>
      ) : null}
      {step.code === 'review' && step.review_submitted ? (
        <div className="ot-step-review">Review submitted</div>
      ) : null}
    </div>
  );
}

const OrderTracking = () => {
  const [orders, setOrders] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orderScope, setOrderScope] = useState('all');
  const [bookFilter, setBookFilter] = useState('');
  const [bookInput, setBookInput] = useState('');
  const [bookListOpen, setBookListOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bookOptions, setBookOptions] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [statusCounts, setStatusCounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [showZeroStatuses, setShowZeroStatuses] = useState(false);
  const bookComboboxRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setBookFilter(bookInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [bookInput]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (bookComboboxRef.current && !bookComboboxRef.current.contains(e.target)) {
        setBookListOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filteredBooks = useMemo(() => {
    const q = bookInput.trim().toLowerCase();
    if (!q) return bookOptions;
    return bookOptions.filter((title) => title.toLowerCase().includes(q));
  }, [bookOptions, bookInput]);

  const pickBook = (title) => {
    setBookInput(title);
    setBookFilter(title);
    setBookListOpen(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await databaseAPI.getProductsList();
      if (cancelled || !res.success) return;
      const titles = [
        ...new Set((res.products || []).map((p) => (p.title || '').trim()).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b));
      if (titles.length) setBookOptions(titles);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadTracking = useCallback(async ({ reset = true, offset = null } = {}) => {
    if (reset) {
      setLoading(true);
      setError('');
    } else {
      setLoadingMore(true);
    }

    try {
      const res = await databaseAPI.getOrdersTracking({
        q: searchQuery,
        status: statusFilter,
        orderScope,
        book: bookFilter,
        dateFrom,
        dateTo,
        limit: 30,
        offset: reset ? null : offset,
      });

      if (!res.success) {
        throw new Error(res.error || 'Failed to load tracking data');
      }

      setStatusOptions(res.status_options || []);
      if (reset) {
        setStatusCounts(res.status_counts || []);
        setTotalCount(res.total_count || 0);
      }
      if (reset && res.book_options?.length) {
        setBookOptions((prev) => {
          const merged = new Set([...prev, ...res.book_options]);
          return [...merged].sort((a, b) => a.localeCompare(b));
        });
      }
      setNextOffset(res.next_offset || null);
      setOrders((prev) => (reset ? res.orders : [...prev, ...res.orders]));
    } catch (e) {
      setError(e.message);
      if (reset) setOrders([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, statusFilter, orderScope, bookFilter, dateFrom, dateTo]);

  useEffect(() => {
    loadTracking({ reset: true });
  }, [loadTracking]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const visibleStepColumns = STEP_COLUMNS;

  const exportFilters = useMemo(
    () => ({
      q: searchQuery,
      status: statusFilter,
      orderScope,
      book: bookFilter,
      dateFrom,
      dateTo,
    }),
    [searchQuery, statusFilter, orderScope, bookFilter, dateFrom, dateTo],
  );

  const visibleStatusCounts = useMemo(() => {
    return statusCounts.filter(
      (item) => showZeroStatuses || item.count > 0 || statusFilter === item.code,
    );
  }, [statusCounts, showZeroStatuses, statusFilter]);

  const hiddenZeroCount = useMemo(
    () => statusCounts.filter((item) => item.count === 0 && statusFilter !== item.code).length,
    [statusCounts, statusFilter],
  );

  const handleExport = async (format) => {
    setExporting(true);
    setError('');
    try {
      await downloadTrackingExportFromServer(databaseAPI, exportFilters, format);
      window.alert(
        `${format.toUpperCase()} export started/downloaded (server-side, max 20,000 rows).`,
      );
    } catch (e) {
      setError(e.message || 'Export failed');
      window.alert(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="order-tracking-page">
      <div className="ot-hero">
        <h1>Order Tracking</h1>
      </div>

      <div className="ot-filters">
        <form className="ot-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Order # (5119) or customer email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit">Search</button>
          {searchQuery ? (
            <button
              type="button"
              className="ot-clear-btn"
              onClick={() => {
                setSearchInput('');
                setSearchQuery('');
              }}
            >
              Clear
            </button>
          ) : null}
        </form>

        <div className="ot-filter-row">
          <label>
            Status
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {statusOptions.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="ot-book-filter" ref={bookComboboxRef}>
            Book
            <div className="ot-combobox">
              <input
                type="text"
                className="ot-combobox-input"
                placeholder="Search book title…"
                value={bookInput}
                onChange={(e) => {
                  setBookInput(e.target.value);
                  setBookListOpen(true);
                }}
                onFocus={() => setBookListOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setBookFilter(bookInput.trim());
                    setBookListOpen(false);
                  }
                  if (e.key === 'Escape') {
                    setBookListOpen(false);
                  }
                }}
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={bookListOpen}
              />
              {bookInput ? (
                <button
                  type="button"
                  className="ot-combobox-clear"
                  aria-label="Clear book filter"
                  onClick={() => pickBook('')}
                >
                  ×
                </button>
              ) : null}
              {bookListOpen && bookOptions.length > 0 ? (
                <ul className="ot-combobox-list" role="listbox">
                  <li
                    role="option"
                    className="ot-combobox-item ot-combobox-item-all"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickBook('')}
                  >
                    All books
                  </li>
                  {filteredBooks.length === 0 ? (
                    <li className="ot-combobox-item muted">
                      No match — press Enter to search &quot;{bookInput.trim()}&quot;
                    </li>
                  ) : (
                    filteredBooks.map((title) => (
                      <li
                        key={title}
                        role="option"
                        className="ot-combobox-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickBook(title)}
                      >
                        {title}
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </div>
          </label>

          <p className="ot-filter-hint">
            {statusFilter
              ? 'Dates filter when the selected status was reached. Counts match the filtered table below.'
              : 'Dates filter by order placement date. Counts match the filtered table below.'}
          </p>

          <label>
            {statusFilter ? 'Status reached from' : 'Order date from'}
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>

          <label>
            {statusFilter ? 'Status reached to' : 'Order date to'}
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>

          <div className="ot-scope-switcher">
            {[
              { value: 'all', label: 'All' },
              { value: 'shipping', label: 'Shipping' },
              { value: 'digital', label: 'Digital' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`ot-scope-pill${orderScope === opt.value ? ' active' : ''}`}
                onClick={() => setOrderScope(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="ot-export-actions">
            <button
              type="button"
              className="ot-export-btn"
              disabled={exporting || loading}
              onClick={() => handleExport('csv')}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button
              type="button"
              className="ot-export-btn ot-export-btn-excel"
              disabled={exporting || loading}
              onClick={() => handleExport('excel')}
            >
              {exporting ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
        </div>
      </div>

      <div className="ot-stats-section">
        <div className="ot-stats-scroll" role="tablist" aria-label="Status overview">
          <button
            type="button"
            className={`ot-stat-pill ot-stat-total${!statusFilter ? ' active' : ''}`}
            onClick={() => setStatusFilter('')}
            title="Show all orders"
          >
            <span>Total</span>
            <strong>{totalCount}</strong>
          </button>
          {visibleStatusCounts.map((item) => (
            <button
              key={item.code}
              type="button"
              className={`ot-stat-pill${statusFilter === item.code ? ' active' : ''}${item.count === 0 ? ' is-empty' : ''}`}
              onClick={() => setStatusFilter(item.code)}
              title={`Filter by ${item.label}`}
            >
              <span>{item.label}</span>
              <strong>{item.count}</strong>
            </button>
          ))}
        </div>
        {hiddenZeroCount > 0 ? (
          <button
            type="button"
            className="ot-stats-toggle"
            onClick={() => setShowZeroStatuses((prev) => !prev)}
          >
            {showZeroStatuses ? 'Hide empty' : `+${hiddenZeroCount}`}
          </button>
        ) : null}
      </div>

      {error ? <div className="ot-error">{error}</div> : null}

      {loading ? (
        <div className="ot-loading">Loading tracking data...</div>
      ) : (
        <div className="ot-table-section">
          <div className="ot-table-wrap">
            <table className="ot-table">
              <thead>
                <tr>
                  <th className="ot-sticky-col">Order</th>
                  <th className="ot-sticky-col ot-col-email">Email</th>
                  <th className="ot-sticky-col ot-col-status">Current Status</th>
                  <th>Type</th>
                  {visibleStepColumns.map((col) => (
                    <th key={col.code} className="ot-step-header">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={4 + visibleStepColumns.length} className="ot-empty">
                      No orders found
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => {
                    const stepMap = Object.fromEntries((order.steps || []).map((s) => [s.code, s]));
                    const pipelineCodes = new Set((order.steps || []).map((s) => s.code));

                    return (
                      <tr key={`${order.order_id}-${order.order_number}`}>
                        <td className="ot-sticky-col">
                          <div className="ot-order-num">{order.order_number}</div>
                          <div className="ot-order-date">{formatDateTime(order.ordered_at)}</div>
                          {order.tracking_number ? (
                            <div className="ot-awb">AWB: {order.tracking_number}</div>
                          ) : null}
                        </td>
                        <td className="ot-sticky-col ot-col-email">
                          <div>{order.customer_email}</div>
                          {order.customer_name ? (
                            <div className="ot-customer-name">{order.customer_name}</div>
                          ) : null}
                        </td>
                        <td className="ot-sticky-col ot-col-status">
                          <span className={`ot-status-badge ${order.is_cancelled ? 'cancelled' : ''}`}>
                            {order.current_status_label}
                          </span>
                          {order.is_digital ? (
                            <span className="ot-type-tag digital">Digital</span>
                          ) : (
                            <span className="ot-type-tag shipping">Shipping</span>
                          )}
                        </td>
                        <td>{order.order_type_label}</td>
                        {visibleStepColumns.map((col) => (
                          <td key={col.code}>
                            <StepCell
                              step={stepMap[col.code]}
                              inPipeline={pipelineCodes.has(col.code)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {nextOffset ? (
            <div className="ot-load-more">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => loadTracking({ reset: false, offset: nextOffset })}
              >
                {loadingMore ? 'Loading...' : 'Load more orders'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
